import { Extension } from "@tiptap/core";
import { formatStyle, parseStyle } from "./styleProps";

/**
 * "Start this block on a new page", as an inline style rather than a node.
 *
 * This app already has a page-break convention and it is not a custom element:
 * a block carrying `page-break-before: always`. noticeHtml.js marks those with
 * a dashed rule, PRESERVED_STYLE_PROPS carries the declaration through the
 * schema, and bac/html_docx.py reads it back when rebuilding the .docx - the
 * "contract" styleProps.js describes. Introducing a <div data-page-break> node
 * instead would round-trip through none of that, so this extension writes the
 * declaration the rest of the stack already knows.
 *
 * Every page after the first opens under the running header (case number +
 * page number) that print and the .docx add. Two ways in, same break:
 *   - Page break (Ctrl+Enter) ends the page at the caret.
 *   - New page ends it after the current paragraph.
 * (A New page used to open with the full ACCB letterhead, marked with
 * `data-page-start="letterhead"`. The marker is no longer in the schema, so
 * older drafts lose it on load and print like any other break.)
 *
 * Neither inserts any text: the case number lives in the running header, not
 * in the body. (Breaks used to insert a bold case-number line; the backend
 * strips those from older drafts - see strip_legacy_break_heads.)
 */

// What we write. bac/html_docx.py reads this name.
const PROPERTY = "page-break-before";
const VALUE = "always";

// What we recognise. The browser normalises the legacy property to the
// modern one whenever the style attribute goes through the CSSOM, so a
// reloaded draft carries "break-before: page" instead. Toggling off has to
// clear whichever spelling is actually there.
const BREAK_PROPS = ["page-break-before", "break-before"];

export const PAGE_BREAK_TYPES = ["paragraph", "heading", "genericTextBlock"];

/**
 * The nearest managed block ancestor of the selection, read from a ProseMirror
 * state rather than from the editor: inside a chain the editor still holds the
 * pre-chain state, so a command would otherwise look at the block as it was
 * before splitBlock() ran.
 */
function blockAt(state, types) {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const node = $from.node(depth);
    if (types.indexOf(node.type.name) !== -1) {
      return { name: node.type.name, node, depth };
    }
  }
  return null;
}

/** Does this block start a new page? (Either kind of break.) */
export function startsPage(node) {
  return parseStyle(node.attrs.style).some(
    ([property]) => BREAK_PROPS.indexOf(property) !== -1
  );
}

/** Add or remove our declaration, leaving every other one untouched. */
function withBreak(style, on) {
  const declarations = parseStyle(style).filter(
    ([property]) => BREAK_PROPS.indexOf(property) === -1
  );
  if (on) declarations.push([PROPERTY, VALUE]);
  return formatStyle(declarations) || null;
}

/** For the toolbar: does the caret sit in a block that starts a new page? */
export function isPageBreak(editor, types = PAGE_BREAK_TYPES) {
  if (!editor) return false;
  const found = blockAt(editor.state, types);
  return found ? startsPage(found.node) : false;
}

export const PageBreak = Extension.create({
  name: "pageBreak",

  addOptions() {
    return { types: PAGE_BREAK_TYPES };
  },

  addCommands() {
    // Mark (or clear) the block at the caret.
    const write = (on) => ({ state, commands }) => {
      const found = blockAt(state, this.options.types);
      if (!found) return false;
      return commands.updateAttributes(found.name, {
        style: withBreak(found.node.attrs.style, on),
      });
    };

    return {
      setPageBreak: () => write(true),
      unsetPageBreak: () => write(false),

      togglePageBreak: () => ({ state, commands }) => {
        const found = blockAt(state, this.options.types);
        if (!found) return false;
        return startsPage(found.node)
          ? commands.unsetPageBreak()
          : commands.setPageBreak();
      },

      /**
       * Word's Ctrl+Enter: end the page at the caret. The rest of the block
       * moves to the next page and continues under the running header.
       */
      insertPageBreak: () => ({ chain }) =>
        chain().splitBlock().command(write(true)).run(),

      /**
       * A fresh page, under the running header like any other. Splits at the
       * END of the current block, so it never tears a paragraph the way
       * insertPageBreak deliberately does.
       */
      newPage: () => ({ chain }) =>
        chain()
          .selectTextblockEnd()
          .splitBlock()
          .command(write(true))
          .run(),

      /**
       * Undo a break. An empty block that is only the marker is deleted
       * outright; a block with text keeps it and loses only the break.
       */
      removePageBreak: () => ({ state, commands }) => {
        const found = blockAt(state, this.options.types);
        if (!found || !startsPage(found.node)) return false;

        if (!found.node.textContent.trim()) {
          const { $from } = state.selection;
          return commands.deleteRange({
            from: $from.before(found.depth),
            to: $from.after(found.depth),
          });
        }
        return commands.unsetPageBreak();
      },
    };
  },

  addKeyboardShortcuts() {
    return {
      "Mod-Enter": () => this.editor.commands.insertPageBreak(),

      /**
       * splitBlock copies the block's attributes onto the new block, so
       * pressing Enter inside a page-break block would start yet another
       * page. Split as normal, then clear the break on the new half only.
       * Every other declaration (font, size, line height) is still inherited.
       */
      Enter: () => {
        if (!isPageBreak(this.editor, this.options.types)) return false;
        return this.editor.chain().splitBlock().unsetPageBreak().run();
      },

      /** Backspace at the very start of a page removes that page break. */
      Backspace: () => {
        const { empty, $from } = this.editor.state.selection;
        if (!empty || $from.parentOffset !== 0) return false;
        if (!isPageBreak(this.editor, this.options.types)) return false;
        return this.editor.commands.removePageBreak();
      },
    };
  },
});

export default PageBreak;
